/**
 * 登录页面
 */
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { saveToken } from '../utils/auth';
import LanguageSwitcher from './LanguageSwitcher';
import FuriganaText from './FuriganaText';
import { logClientError, logClientWarn } from '../utils/safeLog';

// 方 in these auth links is the "person" sense (…方は) → かた. The universal engine reads a 名詞-非自立 方 as
// ほう (correct for stimulus 大きい方=ほう), so the label supplies its own reading (方案C, Codex R16).
const PERSON_KATA_FURIGANA = { 方: 'かた' };

function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(''); // 清除错误提示
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(formData);
      const { access_token } = response.data;

      // 保存token
      saveToken(access_token);

      // 确保token已保存后再跳转
      await new Promise(resolve => setTimeout(resolve, 100));

      // ロールに基づいてリダイレクト先を決定
      try {
        const userResponse = await authAPI.getCurrentUser();
        const role = userResponse.data.role;
        localStorage.setItem('graph_tutor_role', role || 'student');
        navigate('/dashboard');
      } catch (roleErr) {
        // ロール取得失敗時はデフォルトのダッシュボードへ
        logClientWarn('Failed to fetch user role, defaulting to dashboard:', roleErr);
        navigate('/dashboard');
      }
    } catch (err) {
      logClientError('Login error:', err);
      // Store a translation KEY (not a rendered string) so the message re-localizes
      // live when the language is switched. The backend `detail` is Japanese-only and
      // not a key, so it is intentionally not rendered here; per-reason localized
      // errors arrive with backend error codes in Phase 7.
      setError('auth.login.failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 flex items-center justify-center">
      <div className="w-full max-w-lg">
        <div className="mb-2 flex justify-end">
          <LanguageSwitcher />
        </div>
        <div className="text-center mb-4">
          <div
            className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-lg text-3xl"
            style={{ border: '1px solid var(--border)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' }}
          >
            📊
          </div>
          <h1 className="text-2xl font-bold tracking-wide" style={{ color: 'var(--text)' }}>
            Graph Tutor AI
          </h1>
          <p className="mt-0.5 text-base" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('common.appTagline')} />
          </p>
        </div>

        <div
          className="rounded-xl p-10 backdrop-blur"
          style={{
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <h2 className="text-center text-xl font-bold" style={{ color: 'var(--text)' }}>
            {t('auth.login.title')}
          </h2>
          <p className="mt-0.5 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('auth.login.subtitle')} />
          </p>

          {error && (
            <div
              className="mt-6 mb-4 rounded-2xl p-4 text-xs"
              style={{ border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)' }}
            >
              <FuriganaText text={t(error)} />
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-4 space-y-3">
            <div>
              <label htmlFor="login-email" className="mb-1 block text-base font-semibold" style={{ color: 'var(--text)' }}>
                {t('auth.login.emailLabel')}
              </label>
              <input
                id="login-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="input-field rounded-lg"
                placeholder="example@email.com"
                style={{ padding: '12px 16px' }}
              />
            </div>

            <div>
              <label htmlFor="login-password" className="mb-1 block text-base font-semibold" style={{ color: 'var(--text)' }}>
                {t('auth.login.passwordLabel')}
              </label>
              <input
                id="login-password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength="6"
                className="input-field rounded-lg"
                placeholder="••••••••"
                style={{ padding: '12px 16px' }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full rounded-full py-3 text-base transition-transform hover:scale-[1.02] disabled:scale-100"
            >
              <FuriganaText>{loading ? t('auth.login.submitting') : t('auth.login.submit')}</FuriganaText>
            </button>
          </form>

          <div className="mt-4 text-center text-base" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText overrides={PERSON_KATA_FURIGANA}>
              {t('auth.login.noAccount')}{' '}
              <Link
                to="/register"
                className="font-semibold hover:underline"
                style={{ color: 'var(--accent)' }}
              >
                <FuriganaText>{t('auth.login.registerLink')}</FuriganaText>
              </Link>
            </FuriganaText>
          </div>
          <div className="mt-2 text-center">
            <Link
              to="/register?role=teacher"
              className="hover:underline"
              style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}
            >
              <FuriganaText>{t('auth.login.registerTeacherLink')}</FuriganaText>
            </Link>
          </div>
        </div>

        <div className="mt-10 text-center text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          <p><FuriganaText text={t('auth.login.footer.developedBy')} /></p>
          <p><FuriganaText text={t('auth.login.footer.affiliation')} /></p>
          <p>{t('auth.login.footer.contact')}</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
